import {
	QueryKey,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { ApiErrorResponse } from "../models/ApiResponse";
import { CompetenceItem } from "../models/Competence";
import Customer, { CreateCustomerRequest, UpdateCustomerRequest } from "../models/Customer";
import { CustomerRating } from "../models/CustomerRating";
import { TimelinePage } from "../models/EventTimeline";
import CustomerUser, { CreateCustomerUserRequest } from "../models/CustomerUser";
import { isUndefinedOrTrue } from "../utils/object.utils";
import { PERSIST_MAX_AGE, QueryOpts } from "./api-hooks";
import { apiStore } from "../stores/api.store";
import { toast } from "../utils/toast";

export function useCustomersByContract(contractId: string) {
	const { data, ...rest } = useQuery(
		["customers", contractId],
		() =>
			apiStore
				.get<{ customers: Customer[]; totalCount: number }>({
					path: `/customer`,
					query: {
						contractId,
						pageSize: 10000,
					},
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{}
	);

	return {
		...data,
		...rest,
	};
}

export function useUpdateCustomer() {
	const queryClient = useQueryClient();

	return useMutation<
		Customer,
		ApiErrorResponse,
		{ customerId: string; silent?: boolean; update: UpdateCustomerRequest }
	>(
		(params) =>
			apiStore
				.put({
					path: `/customer/${params.customerId}`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.successGeneric();
				}

				// Patch the persisted full lists in place so navigating back to the
				// list shows the change immediately instead of after a full re-page
				// (mirrors useUpdateConsultant). Only the submitted fields are
				// merged — list rows are slim projections. syncVersion is left
				// untouched on purpose; the sync-version watcher reconciles the
				// list in the background.
				queryClient.setQueriesData<AllCustomersData>(["customers", "all"], (old) =>
					old && Array.isArray(old.items)
						? {
								...old,
								// Cast: UpdateCustomerRequest allows null (unset) for fields
								// like corporateGroup where Customer only allows undefined.
								items: old.items.map((c) =>
									c.id === params.customerId ? ({ ...c, ...params.update } as Customer) : c
								),
						  }
						: old
				);

				// Namespace invalidation: refreshes the customer detail and the
				// paged lists; the just-patched full "all" lists are excluded
				// (mirrors useUpdateConsultant).
				queryClient.invalidateQueries({
					queryKey: ["customers"],
					predicate: (query) => query.queryKey[1] !== "all",
				});
			},
			onError: ({ error }, params) => {
				if (!params.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useCreateCustomer() {
	const queryClient = useQueryClient();

	return useMutation<Customer, ApiErrorResponse, CreateCustomerRequest>(
		(body) => {
			// The backend rejects an empty invoice email; drop it rather than fail.
			if (!body.invoiceDetails?.email) delete body.invoiceDetails?.email;

			return apiStore
				.post({
					path: `/customer`,
					body,
					isAuthRequired: true,
				})
				.then((d) => d.data);
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["customers"]);
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

/**
 * Set a customer's tags with a true optimistic update — same pattern as
 * `useToggleConsultantTag`. Customer tags are plain `{ key }` labels the client
 * computes fully, and the backend stores them verbatim (it only validates the
 * keys against the org's customerTagTypes), so the optimistic value is the
 * final value. Caches that render customer tags are patched in `onMutate` and
 * rolled back on error; `onSuccess` writes the customer returned by the PUT
 * back; `onSettled` only marks the customers namespace stale
 * (`refetchType: "none"`) so the mounted full list doesn't re-page thousands of
 * rows on every toggle.
 */
export function useToggleCustomerTag() {
	const queryClient = useQueryClient();

	const patchCaches = (customerId: string, tags: { key: string }[]) => {
		// The full per-pool lists backing the virtualized list's tag chips.
		queryClient.setQueriesData<AllCustomersData>(["customers", "all"], (old) =>
			old && Array.isArray(old.items)
				? {
						...old,
						items: old.items.map((c) => (c.id === customerId ? { ...c, tags } : c)),
				  }
				: old
		);

		// Customer detail caches. The ["customers", customerId] prefix could also
		// match non-detail caches (e.g. useCustomersByContract keys by contract
		// id), so only patch entries that are the customer object itself.
		queryClient.setQueriesData<Customer>(["customers", customerId], (old) =>
			old && !Array.isArray(old) && (old as Customer).id === customerId ? { ...old, tags } : old
		);
	};

	return useMutation<
		Customer,
		ApiErrorResponse,
		{ customerId: string; nextTags: { key: string }[] },
		{ entries: [QueryKey, unknown][] }
	>(
		(params) =>
			apiStore
				.put({
					path: `/customer/${params.customerId}`,
					body: { tags: params.nextTags },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onMutate: async ({ customerId, nextTags }) => {
				// Stop in-flight refetches from overwriting the optimistic patch.
				await queryClient.cancelQueries(["customers"]);

				// Snapshot every cache we are about to patch, for rollback on error.
				const entries: [QueryKey, unknown][] = [
					...queryClient.getQueriesData(["customers", "all"]),
					...queryClient.getQueriesData(["customers", customerId]),
				];

				patchCaches(customerId, nextTags);

				return { entries };
			},
			onError: ({ error }, _params, context) => {
				context?.entries.forEach(([key, data]) => queryClient.setQueryData(key, data));
				toast.apiError(error.code);
			},
			onSuccess: (updated, { customerId }) => {
				if (updated?.id) {
					patchCaches(customerId, updated.tags ?? []);
				}
			},
			onSettled: () => {
				// Mark stale without refetching mounted queries; reconciles lazily.
				queryClient.invalidateQueries({ queryKey: ["customers"], refetchType: "none" });
			},
		}
	);
}

export type GetCustomersParams = {
	page: number;
	pageSize: number;
	keyword?: string;
	pool?: string;
	sort?: string;
	order?: string;
	expand?: string[];
	active?: boolean;
	isSignup?: boolean;
	/** Backend projection, e.g. ViewModels.SELECT_OPTION for slim dropdown data. */
	view?: string;
};

export function useCustomers(query: GetCustomersParams, enabled?: boolean) {
	const { data, ...rest } = useQuery(
		["customers", query],
		() =>
			apiStore
				.get<{ totalCount: number; customers: Customer[] }>({
					path: "/customer",
					isAuthRequired: true,
					query: {
						...query,
						expand: query.expand ? query.expand.join(",") : "",
					},
				})
				.then((d) => d.data),
		{
			keepPreviousData: true,
			enabled,
		}
	);

	return {
		customers: data,
		...rest,
	};
}

/** Cached shape of the full customer list, tagged with the server's sync version. */
export type AllCustomersData = {
	syncVersion: number;
	items: Customer[];
};

/** Current server-side sync version for customers (per org). */
export async function fetchCustomerSyncVersion(): Promise<number> {
	const { data } = await apiStore.get<{ version: number }>({
		path: "/customer-sync-version",
		isAuthRequired: true,
	});

	return data.version;
}

/**
 * Fetch the *entire* customer list (optionally scoped to a pool) by paging
 * through the endpoint in large batches. Backs the virtualized customer list,
 * which holds the whole set in memory and filters/searches client-side. Mirrors
 * `fetchAllConsultants` — including fetching the sync version BEFORE paging,
 * so a write landing mid-page costs one extra refetch rather than staleness.
 */
async function fetchAllCustomers(pool?: string): Promise<AllCustomersData> {
	const syncVersion = await fetchCustomerSyncVersion();

	const batchSize = 5000;
	const all: Customer[] = [];
	let page = 1;
	let total = Infinity;

	while (all.length < total) {
		const { data } = await apiStore.get<{ totalCount: number; customers: Customer[] }>({
			path: "/customer",
			isAuthRequired: true,
			query: {
				pool: pool || undefined,
				page,
				pageSize: batchSize,
			},
		});

		const customers = data.customers || [];
		all.push(...customers);
		total = data.totalCount ?? all.length;

		if (customers.length < batchSize) break;
		page++;
	}

	return { syncVersion, items: all };
}

/**
 * React Query owner of the full customer list per pool, persisted to IndexedDB
 * (see shouldDehydrateQuery in App.tsx). Filter/search/sort are client-side
 * over the cached result. The list never goes stale on its own
 * (staleTime: Infinity) — useSyncVersionWatcher invalidates it when the
 * server-side sync version changes. cacheTime must match the persistence
 * window so an unmounted list isn't garbage-collected before the persister
 * writes (which would persist a cache WITHOUT the list).
 */
export function useAllCustomers(pool?: string, enabled = true) {
	const { data, ...rest } = useQuery(
		["customers", "all", pool ?? ""],
		() => fetchAllCustomers(pool),
		{
			staleTime: Infinity,
			cacheTime: PERSIST_MAX_AGE,
			enabled,
		}
	);

	return {
		...rest,
		customers: data?.items ?? [],
	};
}

export function useCustomer(customerId?: string, enabled?: boolean, expand?: string[]) {
	const { data, ...rest } = useQuery(
		// The params object mirrors useConsultant so callers with different
		// expands don't share (and overwrite) each other's cache entries.
		["customers", customerId, { expand: expand ?? [] }],
		() =>
			apiStore
				.get<Customer>({
					path: `/customer/${customerId}`,
					isAuthRequired: true,
					query: {
						expand: expand ? expand.join(",") : "",
					},
				})
				.then((d) => d.data),
		{
			enabled: !!customerId && isUndefinedOrTrue(enabled),
		}
	);

	return {
		customer: data,
		...rest,
	};
}

/** Aggregated job-report rating for a customer (average + report count). */
export function useCustomerRating(customerId?: string) {
	const { data, ...rest } = useQuery(
		["customer-rating", customerId],
		() =>
			apiStore
				.get<CustomerRating>({
					path: `/rating/customer/${customerId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ enabled: !!customerId }
	);

	return {
		rating: data,
		...rest,
	};
}

export type GetCustomerUsersParams = {
	keyword?: string;
	pool?: string;
	sort?: string;
	order?: string;
	expand?: string[];
	page?: string | number;
	pageSize?: string | number;
	active?: boolean;
};

export function useCustomerUsers(query: GetCustomerUsersParams, enabled?: boolean) {
	const { data, ...rest } = useQuery(
		["customer-users", query],
		() =>
			apiStore
				.get<{ totalCount: number; users: CustomerUser[] }>({
					path: "/customer-account",
					isAuthRequired: true,
					query: {
						...query,
						page: (query.page || "1").toString(),
						pageSize: (query.pageSize || "200").toString(),
						expand: query.expand ? query.expand.join(",") : "",
					},
				})
				.then((d) => d.data),
		{
			enabled,
		}
	);

	const { users, ...restOfData } = data || {};

	return {
		...rest,
		...restOfData,
		customerUsers: users,
	};
}

/**
 * Infinite-scroll variant of {@link useCustomerUsers} for the virtualized
 * customer-users list. Keyword/status filtering happens server-side (GET
 * /customer-account supports `keyword` + `active`), so the list is correct
 * for organisations with more customer users than a single page can hold.
 * `getNextPageParam` derives the next page from `totalCount` vs the number of
 * users loaded so far; rows are de-duped by id to absorb offset drift.
 */
export function useCustomerUsersInfinite(
	query: Omit<GetCustomerUsersParams, "page">,
	opts: QueryOpts = {}
) {
	const pageSize = Number(query.pageSize || 200);

	const infinite = useInfiniteQuery(
		["customer-users", "infinite", query],
		({ pageParam = 1 }) =>
			apiStore
				.get<{ totalCount: number; users: CustomerUser[] }>({
					path: "/customer-account",
					isAuthRequired: true,
					query: {
						...query,
						page: String(pageParam),
						pageSize: String(pageSize),
						expand: query.expand ? query.expand.join(",") : "",
					},
				})
				.then((d) => d.data),
		{
			getNextPageParam: (lastPage, allPages) => {
				const loaded = allPages.reduce((sum, page) => sum + (page?.users?.length ?? 0), 0);
				const totalCount = allPages[0]?.totalCount ?? 0;
				return loaded < totalCount && (lastPage?.users?.length ?? 0) > 0
					? allPages.length + 1
					: undefined;
			},
			keepPreviousData: true,
			...opts,
		}
	);

	const customerUsers = useMemo(() => {
		const seen = new Set<string>();
		return (infinite.data?.pages ?? [])
			.flatMap((page) => page?.users ?? [])
			.filter((user) => !!user.id && !seen.has(user.id) && !!seen.add(user.id));
	}, [infinite.data]);

	const totalCount = infinite.data?.pages[0]?.totalCount ?? 0;

	return { customerUsers, totalCount, ...infinite };
}

export function useCustomerUser(userId?: string, enabled?: boolean) {
	const { data, ...rest } = useQuery(
		["customer-users", userId],
		() =>
			apiStore
				.get<CustomerUser>({
					path: "/customer-account/" + userId,
					isAuthRequired: true,
					query: {},
				})
				.then((d) => d.data),
		{
			enabled: enabled && !!userId,
		}
	);

	return {
		...rest,
		customerUser: data,
	};
}

export function useCustomersByCustomerUser(userId?: string, enabled?: boolean) {
	const { data, ...rest } = useQuery(
		["customer-users", userId, "customers"],
		() =>
			apiStore
				.get<{ customers: Customer[] }>({
					path: `/customer-account/${userId}/customer`,
					isAuthRequired: true,
					query: {},
				})
				.then((d) => d.data),
		{
			enabled: enabled && !!userId,
		}
	);

	return {
		...rest,
		customers: data?.customers,
	};
}

export function useCustomerUsersByCustomer(
	{ customerId, ...query }: GetCustomerUsersParams & { customerId: string },
	enabled?: boolean
) {
	const { data, ...rest } = useQuery(
		["customer-users", customerId, query],
		() =>
			apiStore
				.get<{ totalCount: number; users: CustomerUser[] }>({
					path: `/customer/${customerId}/user`,
					isAuthRequired: true,
					query: {
						...query,
						expand: query.expand ? query.expand.join(",") : "",
					},
				})
				.then((d) => d.data),
		{
			enabled,
		}
	);

	const { users, ...restOfData } = data || {};

	return {
		...rest,
		...restOfData,
		customerUsers: users,
	};
}

/**
 * Create a customer user (customer-account). Like useUpdateCustomerUser /
 * useDeleteCustomerUser this hook is toast-silent — callers own the feedback.
 */
export function useCreateCustomerUser() {
	const queryClient = useQueryClient();

	return useMutation<CustomerUser, ApiErrorResponse, CreateCustomerUserRequest>(
		(body) =>
			apiStore
				.post({
					path: `/customer-account`,
					body,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["customer-users"]);
			},
		}
	);
}

export function useUpdateCustomerUser() {
	const queryClient = useQueryClient();

	return useMutation(
		(params: { userId: string; update: Partial<CustomerUser> }) =>
			apiStore
				.put({
					path: `/customer-account/${params.userId}`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				queryClient.invalidateQueries(["customer-users"]);
			},
		}
	);
}

/**
 * Set a customer user's password directly (admin action) — the
 * customer-account counterpart of `useSetConsultantPassword`.
 */
export function useSetCustomerUserPassword() {
	return useMutation<any, ApiErrorResponse, { userId: string; newPassword: string }>(
		(params) =>
			apiStore
				.put({
					path: `/customer-account/${params.userId}/password`,
					body: { newPassword: params.newPassword },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

export function useDeleteCustomerUser() {
	const queryClient = useQueryClient();

	return useMutation(
		(userId: string) =>
			apiStore
				.delete({
					path: `/customer-account/${userId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				queryClient.invalidateQueries(["customer-users"]);
			},
		}
	);
}

export function useCompetencesByCustomer(
	params: { customerId?: string; inherited?: boolean },
	enabled?: boolean
) {
	const { customerId, ...query } = params;

	const { data, ...rest } = useQuery(
		["customer", "competences", params],
		() =>
			apiStore
				.get<{ competences: CompetenceItem[]; totalCount: number }>({
					path: `/customer/${customerId}/competences`,
					query: {
						...query,
					},
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{ enabled: !!customerId && isUndefinedOrTrue(enabled) }
	);

	return {
		competences: data?.competences,
		...rest,
	};
}

export function useCustomerDuplicates(customerId: string) {
	return useQuery(["customer-duplicates", customerId], () =>
		apiStore
			.get<{ customers: { id: string; customerNumber: string; name: string }[] }>({
				path: `/customer/${customerId}/duplicates`,
				isAuthRequired: true,
			})
			.then((d) => d.data)
	);
}

// TODO: need to change this when BE is ready
export function useCustomerRatings(
	startTime: string,
	endTime: string,
	customerIds?: string[],
	expand?: string[],
	enabled?: boolean
) {
	//
	// const { data, ...rest } = useQuery(
	// 	["customer-ratings", customerIds, startTime, endTime, expand],
	// 	() =>
	// 		apiStore
	// 			.get<{ ratings: CustomerRating[] }>({
	// 				path: `/consultant_rating`,
	// 				isAuthRequired: true,
	// 				query: {
	// 					startTime,
	// 					endTime,
	// 					customerIds: customerIds ? customerIds.join(","): "",
	// 					expand: expand ? expand.join(",") : "",
	// 				},
	// 			})
	// 			.then((res) => res.data),
	// 	{ enabled: !!(startTime && endTime) && isUndefinedOrTrue(enabled), refetchOnWindowFocus: false },
	// );

	// return {
	// 	...data,
	// 	...rest,
	// };
	return {
		isLoading: false,
		ratings: [],
	};
}

/**
 * The customer event timeline ("Händelser"), newest first, one page at a time.
 * Fetched by the history tab on activation.
 *
 * Paged with an opaque cursor rather than an offset: entries written by one
 * save share a timestamp, so the backend pages on a (date, id) tuple and hands
 * back the cursor to continue from.
 */
export function useCustomerHistory({ customerId }: { customerId?: string }, opts: QueryOpts = {}) {
	const infinite = useInfiniteQuery(
		["customers", customerId, "history"],
		({ pageParam }) =>
			apiStore
				.get<TimelinePage>({
					path: `/customer/${customerId}/history`,
					isAuthRequired: true,
					query: pageParam ? { cursor: pageParam } : undefined,
				})
				.then((d) => d.data),
		{
			getNextPageParam: (lastPage) => (lastPage?.hasMore ? lastPage.nextCursor : undefined),
			enabled: !!customerId && isUndefinedOrTrue(opts.enabled),
			...opts,
		}
	);

	const entries = useMemo(
		() => infinite.data?.pages.flatMap((page) => page?.entries ?? []),
		[infinite.data]
	);

	return { entries, ...infinite };
}
